// utils/helpers/helper.general.js
import _find from 'lodash/find';
import R from 'ramda';

const {
  apply,
  map,
  reverse,
  split,
  pipe,
  curry,
  filter,
  ascend,
  descend,
  contains,
  prop,
  path,
  compose,
  sortWith,
  any,
  propEq,
} = R;

// helper functions based on ramda
export const makeSortAscendFn = compose(
  ascend,
  pipe(
    split('.'),
    reverse,
    map(prop),
    apply(compose),
  ),
);
export const makeSortDescendFn = compose(
  descend,
  pipe(
    split('.'),
    reverse,
    map(prop),
    apply(compose),
  ),
);

/*
 * Sort list by params in the item object
 * sort.props: array of string
 */

export const sortList = (initList, sort) => {
  if (sort.ascend) {
    const props = sort.props.map(prop => makeSortAscendFn(prop));
    return sortWith(props, initList);
  } else {
    const props = sort.props.map(prop => makeSortDescendFn(prop));
    return sortWith(props, initList);
  }
};

// remove empty object items
const clean = initObj => {
  let obj = { ...initObj };
  for (let propName in obj) {
    const isStr = typeof obj[propName] === 'string';
    const isObj = typeof obj[propName] === 'object';
    const isArray = Array.isArray(obj[propName]);
    if (
      (propName != ('appointments' || 'forms' || 'documents' || 'discussion')) && 
      (
        (typeof obj === 'object' &&
        obj.hasOwnProperty(propName) &&
        (!isArray &&
          (obj[propName] === null ||
            obj[propName] === undefined ||
            obj[propName] === ''))) ||
        (isArray && obj[propName].length == 0) ||
        (isObj && Object.keys(obj[propName]).length == 0)
      )
    ) {
      delete obj[propName];
    }
  }
  return obj;
};

let hasUrl = text => {
  let urlFindRegex = /((https?|ftp)\:\/\/)?([a-z0-9+!*(),;?&=\$_.-]+(\:[a-z0-9+!*(),;?&=\$_.-]+)?@)?([a-z0-9-.]*)\.([a-z]{2,3})(\:[0-9]{2,5})?(\/([a-z0-9+\$_\-~@\(\)\%]\.?)+)*\/?(\?[a-z+&\$_.-][a-z0-9;:@&#%=+\/\$_.-]*)?(#[a-z_.-][a-z0-9+\$_.-]*)?/i;
  return urlFindRegex.test(text);
};

const urlify = text => {
  // search for http , https , ftp , and file URLs.
  const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
  return text.replace(urlRegex, url => `<a href="${url}">${url}</a>`);
};

/**
 * Truncate a string over a given length and add ellipsis if necessary
 * @param {string} str - string to be truncated
 * @param {integer} limit - max length of the string before truncating
 * @return {string} truncated string
 */
const truncate = function(str, limit) {
  return str.length < limit
    ? str
    : str.substring(0, limit).replace(/\w{3}$/gi, '...');
};

function removeTags(str) {
  if (str === null || str === '') return false;
  else str = str.toString();

  // Regular expression to identify HTML tags in
  // the input string. Replacing the identified
  // HTML tag with a null string.
  return str.replace(/(<([^>]+)>)/gi, '');
}

/**
 * Returns the first `limit` characters from the given `string`.
 *
 * @param {String} string
 * @param {Number} limit
 *
 * @returns {String}
 */
function limit(string = '', limit = 0) {
  const str = removeTags(string);
  return str && str.substring(0, limit);
}

/*
 * Returns a function which will sort an
 * array of objects by the given key.
 */
const sortByKey = (key, reverse) => {
  // Move smaller items towards the front
  // or back of the array depending on if
  // we want to sort the array in reverse
  // order or not.
  const moveSmaller = reverse ? 1 : -1;

  // Move larger items towards the front
  // or back of the array depending on if
  // we want to sort the array in reverse
  // order or not.
  const moveLarger = reverse ? -1 : 1;

  /**
   * @param  {*} a
   * @param  {*} b
   * @return {Number}
   */
  return (a, b) => {
    if (a.hasOwnProperty(key)) {
      if (a[key] < b[key]) {
        return moveSmaller;
      }
      if (a[key] > b[key]) {
        return moveLarger;
      }
    }
    return 0;
  };
};

function getObjId(dataObj) {
  let currentId = '';
  if (dataObj) {
    if (typeof dataObj === 'object') {
      currentId = dataObj['_id'];
    } else {
      currentId = dataObj;
    }
  }
  return currentId;
}

function getArrayIds(array) {
  let idsArray = [];
  if (!!array && Array.isArray(array) && array?.length > 0) {
    for (let i = 0; i < array.length; i++) {
      if (typeof array[i] === 'object') {
        idsArray[i] = array[i]['_id'];
      } else {
        idsArray[i] = array[i];
      }
    }
  }
  return idsArray;
}

function ArrayEquals(arr1, arr2) {
  if (arr1?.length === arr2?.length) {
    return (
      arr1?.every(v => arr2.includes(v)) &&
      arr2?.every(v => arr1.includes(v))
    );
  } else {
    return false;
  }
}
export function objectsAreSame(x, y) {
  let objectsAreSame = [];
  for (let propertyName in x) {
    if (
      !!x[propertyName] &&
      !!y[propertyName] &&
      !Array.isArray(x[propertyName]) &&
      !Array.isArray(y[propertyName]) &&
      JSON.stringify(x[propertyName]) != JSON.stringify(y[propertyName])
    ) {
      objectsAreSame.push('false')
    } else if (
      !!x[propertyName] &&
      !!y[propertyName] &&
      Array.isArray(x[propertyName]) &&
      Array.isArray(y[propertyName])
    ) {
      const arrayEqual = arraysObjEqual(x[propertyName], y[propertyName]);
      if (!arrayEqual) {
        objectsAreSame.push(!arrayEqual)
      }
    }
  }
  return objectsAreSame?.length > 0 && objectsAreSame.every(el => el == true);
}

export function filterObject(obj, keys) {
  return keys.reduce((filteredObj, key) => {
    if (!!obj && obj?.hasOwnProperty(key)) {
      filteredObj[key] = obj?.[key];
    }
    return filteredObj;
  }, {});
}

export function compareArrays(arr1, arr2) {
  if (arr1?.length != arr2?.length) {
    return false;
  }
  return arr1?.every((v, i) => {
    const sameArr = isEqualValue(arr1?.[i], arr2?.[i])
    return sameArr
  });
}

function isEqualValue(value1, value2) {
  // Handle special cases for null and undefined
  if (value1 === null && value2 === null) {
    return true;
  }

  if (typeof value1 === 'undefined' && typeof value2 === 'undefined') {
    return true;
  }

  // Recursively compare objects
  if (typeof value1 === 'object' && typeof value2 === 'object') {
    if (Array.isArray(value1) && Array.isArray(value2)) {
      // Compare arrays
      return compareArrays(value1, value2);
    } else {
      // Compare objects
      return compareObjectsWithDiff(value1, value2);
    }
  } else if (Array.isArray(value1) && Array.isArray(value2)) {
    return compareArrays(x?.[key], y?.[key])
  } else if (typeof value1 === 'string' && typeof value2 === 'string' && !!value1 && !!value2) {
    // match any HTML tags and whitespace
    const regex = /<[^>]*>|^\s*$/gm; 
    // remove any HTML tags and whitespace
    const actualText = value1.replace(regex, '').replace('\n', ''); 
    const prevText = value2.replace(regex, '').replace('\n', ''); 
    return prevText == actualText
  }

  // Compare values directly
  return JSON.stringify(value1) == JSON.stringify(value2);
}

export function compareObjectsWithDiff(obj1, obj2) {
  // Check if both objects are null or undefined
  if (
    obj1 === null && obj2 === null ||
    typeof obj1 === 'undefined' && typeof obj2 === 'undefined' ||
    obj1 === null && (!!!obj2 && typeof obj2 === 'string') ||
    (!!!obj1 && typeof obj1 === 'string') && obj2 === null
  ) {
    return true;
  }

  // Check if only one object is null or undefined
  if ((obj1 === null && typeof obj2 !== 'null') ||
      (typeof obj1 !== 'null' && obj2 === null) ||
      (typeof obj1 === 'undefined' && obj2 !== undefined) ||
      (obj1 !== undefined && typeof obj2 === 'undefined')) {
    return false;
  }

  // Check if the objects have the same keys
  const obj1Keys = Object.keys(obj1);
  const obj2Keys = Object.keys(obj2);

  // Recursively compare the values of each key
  if (obj1Keys?.length != obj2Keys?.length) {
    const same = Object.keys(obj1)?.every(key => !!key && isEqualValue(obj1?.[key], obj2?.[key]))
    return same;
  } else if (obj1Keys?.length == obj2Keys?.length) {
    const allSame = Object.keys(obj1)?.every(key => {
      const isSame = !!key && isEqualValue(obj1?.[key], obj2?.[key])
      /* if (!isSame) {
        console.log('key', key, isSame)
        console.log(obj1?.[key], obj2?.[key])
      } */
      return isSame
    });
    return allSame
  }

  return true;
}

export function objectsAreSameByKeys(x, y, keys) {
  let objectsAreSame = [];
  if (!!x && !!y) {
    for (const key of keys) {
      // Check if both objects are null or undefined
      if (
        x?.[key] === null && y?.[key] === null ||
        typeof obj1 === 'undefined' && typeof y?.[key] === 'undefined' ||
        x?.[key] === null && !!!y?.[key] && typeof y?.[key] === 'string' ||
        !!!x?.[key] && typeof x?.[key] === 'string' && !!!y?.[key] === null
      ) {
        objectsAreSame.push('true');
      } 
        
      // Check if only one object is null or undefined
      else if ((x?.[key] === null && typeof y?.[key] !== 'null') ||
          (typeof x?.[key] !== 'null' && y?.[key] === null) ||
          (typeof x?.[key] === 'undefined' && y?.[key] !== undefined) ||
          (x?.[key] !== undefined && typeof y?.[key] === 'undefined')) {
        objectsAreSame.push('false');
      }
      
      else if (!!x?.[key] && !!y?.[key]) {
        let hasSameKeysVal = true;
        if (Array.isArray(x?.[key]) && Array.isArray(y?.[key])) {
          const sameArrs = compareArrays(x?.[key], y?.[key]);
          hasSameKeysVal = sameArrs;
        }
        else if (typeof x?.[key] == 'object' && typeof y?.[key] == 'object') {
          const compared = compareObjectsWithDiff(x?.[key], y?.[key]);
          hasSameKeysVal = compared
        }
        else if (typeof x?.[key] == 'string' && typeof y?.[key] == 'string') {
          hasSameKeysVal = isEqualValue(x?.[key], y?.[key])
        }
        else {
          hasSameKeysVal =
            JSON.stringify(x?.[key]) == JSON.stringify(y?.[key]);
        }

        if (!!hasSameKeysVal) {
          objectsAreSame.push('true');
        } else {
          objectsAreSame.push('false');
        }

      } 

    }
  } else {
    objectsAreSame.push('true');
  }
  objectsAreSame.push('true');
  
  return (
    objectsAreSame?.length > 0 && objectsAreSame.every(el => el == 'true')
  );
}

export const objectsEqual = (o1, o2) =>
  o1 &&
  o2 &&
  Object.keys(o1)?.length === Object?.keys(o2)?.length &&
  Object.keys(o1)?.every(p => p && o1[p] === o2[p]);

export const arraysObjEqual = (a1, a2) =>
  a1?.length === a2?.length && a1?.every((o, idx) => objectsEqual(o, a2[idx]));

function ArrayEquals2(arr1, arr2) {
  return (
    arr1?.length === arr2?.length &&
    !arr1?.some(v => !!v && arr2?.includgetUniqueArrayObjes(v)) &&
    !arr2?.some(v => !!v && arr1?.includes(v))
  );
}

// converts number to string representation with K and M.
// toFixed(d) returns a string that has exactly 'd' digits
// after the decimal place, rounding if necessary.
function numFormatter(num) {
  if (num > 999 && num < 1000000) {
    return (num / 1000).toFixed(1) + 'k members'; // convert to K for number from > 1000 < 1 million
  } else if (num > 1000000) {
    return (num / 1000000).toFixed(1) + 'm members'; // convert to M for number from > 1 million
  } else if (num < 900) {
    return num + ' members'; // if value < 1000, nothing to do
  }
}

const getUniqueArrayObj = (arrayObj, prop = "_id") => {
  const result = [];
  const map = new Map();
  if (!!arrayObj && Array.isArray(arrayObj) && arrayObj?.length > 0) {
    for (const item of arrayObj) {
      if (!map.has(item[prop])) {
        map.set(item[prop], true); // set any value to Map
        result.push({ ...item });
      }
    }
  }
  return result;
};

export const getUniqueArrayValue = (arrayValues = []) => {
  const result = [];
  const map = new Map();
  if (!!arrayValues && Array.isArray(arrayValues) && arrayValues.length > 0) {
    for (const item of arrayValues) {
      if (!map.has(JSON.stringify(item))) {
        map.set(JSON.stringify(item), true); // set any value to Map
        result.push(item);
      }
    }
  }
  return result;
};


const ext = url => {
  return (url = url.substr(1 + url.lastIndexOf('/')).split('?')[0])
    .split('#')[0]
    .substr(url.lastIndexOf('.'))
    .split('.')
    .pop();
};

function setLocalWithExpiry(key, value, ttl) {
  const now = new Date();

  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

function getLocalWithExpiry(key) {
  const itemStr = localStorage.getItem(key);
  // if the item doesn't exist, return null
  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr);
  const now = new Date();
  // compare the expiry time of the item with the current time
  if (now.getTime() > item.expiry) {
    // If the item is expired, delete the item from storage
    // and return null
    localStorage.removeItem(key);
    return null;
  }
  return item?.value;
}

function objectEquals(x, y) {
  if (x === y) return true;
  // if both x and y are null or undefined and exactly the same

  if (!(x instanceof Object) || !(y instanceof Object)) return false;
  // if they are not strictly equal, they both need to be Objects

  if (x.constructor !== y.constructor) return false;
  // they must have the exact same prototype chain, the closest we can do is
  // test there constructor.

  for (let p in x) {
    if (!x.hasOwnProperty(p)) continue;
    // other properties were tested using x.constructor === y.constructor

    if (!y.hasOwnProperty(p)) return false;
    // allows to compare x[ p ] and y[ p ] when set to undefined

    if (x[p] === y[p]) continue;
    // if they have the same strict value or identity then they are equal

    if (typeof x[p] !== 'object') return false;
    // Numbers, Strings, Functions, Booleans must be strictly equal

    if (!object_equals(x[p], y[p])) return false;
    // Objects and Arrays must be tested recursively
  }

  for (let p in y)
    if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) return false;
  // allows x[ p ] to be set to undefined

  return true;
}

export default function isClosedToPublic() {
  if (window.location.href.includes('/e/')) {
    return false;
  } else {
    return true;
  }
}

/*!
 * Add items to an object at a specific path
 * (c) 2021 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Object}       obj  The object
 * @param  {String|Array} path The path to assign the value to
 * @param  {*}            val  The value to assign
 */
function setToObjectByPath (obj, path, val) {

	/**
	 * If the path is a string, convert it to an array
	 * @param  {String|Array} path The path
	 * @return {Array}             The path array
	 */
	function stringToPath (path) {

		// If the path isn't a string, return it
		if (typeof path !== 'string') return path;

		// Create new array
		let output = [];

		// Split to an array with dot notation
		path.split('.').forEach(function (item) {

			// Split to an array with bracket notation
			item.split(/\[([^}]+)\]/g).forEach(function (key) {

				// Push to the new array
				if (key.length > 0) {
					output.push(key);
				}

			});

		});

		return output;

	}

	// Convert the path to an array if not already
	path = stringToPath(path);

	// Cache the path length and current spot in the object
	let length = path.length;
	let current = obj;

	// Loop through the path
	path.forEach(function (key, index) {

		// Check if the assigned key should be an array
		let isArray = key.slice(-2) === '[]';

		// If so, get the true key name by removing the trailing []
		key = isArray ? key.slice(0, -2) : key;

		// If the key should be an array and isn't, create an array
		if (isArray && !Array.isArray(current[key])) {
			current[key] = [];
		}

		// If this is the last item in the loop, assign the value
		if (index === length -1) {

			// If it's an array, push the value
			// Otherwise, assign it
			if (isArray) {
				current[key].push(val);
			} else {
				current[key] = val;
			}
		}

		// Otherwise, update the current place in the object
		else {

			// If the key doesn't exist, create it
			if (!current[key]) {
				current[key] = {};
			}

			// Update the current place in the object
			current = current[key];

		}

	});

}

function slugify(title) {
  return !!title && title
    .trim()
    .replace(/ +/g, '-')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
}


export {
  setLocalWithExpiry,
  getLocalWithExpiry,
  sortByKey,
  clean,
  getObjId,
  ArrayEquals,
  ArrayEquals2,
  getArrayIds,
  numFormatter,
  getUniqueArrayObj,
  ext,
  hasUrl,
  urlify,
  truncate,
  objectEquals,
  setToObjectByPath,
  slugify
};